import React, { MutableRefObject, useCallback, useRef } from 'react';
import { StyledButton, RippleContainer, Content } from './styles';
import { Spinner } from '../Spinner';

export const Button = ({
  onClick,
  loading,
  children,
  secondary,
  className,
  ...rest
}: ButtonProps) => {
  const rippleContainerRef = useRef() as React.RefObject<HTMLSpanElement>;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const x = e.pageX - rect.left;
      const y = e.pageY - rect.top;

      if (rippleContainerRef.current) {
        // Create the ripple element
        const ripple = document.createElement('span');
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        ripple.classList.add('ripple');

        // Append the ripple to the container
        rippleContainerRef.current.appendChild(ripple);

        // Remove the ripple element after the animation completes
        ripple.addEventListener('animationend', () => {
          ripple.remove();
        });
      }

      if (onClick) {
        onClick(e);
      }
    },
    [onClick],
  );

  return (
    <StyledButton
      {...rest}
      $secondary={secondary}
      onClick={handleClick}
      disabled={rest.disabled || loading}
      $isLoading={loading}
      className={className}
    >
      {loading && <Spinner scale={0.6} color={secondary ? 'blue' : 'white'} />}
      <Content $isLoading={loading}>{children}</Content>
      <RippleContainer ref={rippleContainerRef} />
    </StyledButton>
  );
};

export interface ButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  secondary?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  className?: string;
  ref?: MutableRefObject<any>;
}
